import React, { useMemo } from 'react';
import { computeDurationSummary, computeSessionSummary, SESSION_COLORS } from '../../../utils/tradingSessions';
import { formatCurrency } from '../charts/chartTheme';

/**
 * WinPercentBar – visual bar showing win% (green) vs loss% (red) on a grey track
 */
const WinPercentBar = ({ winPercent }) => {
  if (winPercent === null) return <span className="text-muted-foreground text-sm">--</span>;
  const lossPercent = 100 - winPercent;
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden flex">
        <div
          className="h-full rounded-l-full"
          style={{ width: `${lossPercent}%`, backgroundColor: '#ef4444' }}
        />
        <div
          className="h-full rounded-r-full"
          style={{ width: `${winPercent}%`, backgroundColor: '#22c55e' }}
        />
      </div>
    </div>
  );
};

/**
 * Duration Summary Table
 * Shows trade stats grouped by hold duration (like prop-firm reports)
 */
const DurationSummaryTable = ({ trades }) => {
  const data = useMemo(() => computeDurationSummary(trades), [trades]);
  const hasAnyTrades = data.some(d => d.tradeCount > 0);

  if (!hasAnyTrades) {
    return (
      <div className="text-center text-muted-foreground py-8">
        <p>No trades with duration data available</p>
        <p className="text-xs mt-1">Trades need both open and close times</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground border-b border-border">
            <th className="text-left py-3 px-3 font-medium">Duration</th>
            <th className="text-center py-3 px-3 font-medium">Net Profits</th>
            <th className="text-center py-3 px-3 font-medium min-w-[140px]">Winning %</th>
            <th className="text-center py-3 px-3 font-medium">Total Profits</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={row.label}
              className={`border-b border-border/50 ${i % 2 === 0 ? 'bg-muted/20' : ''}`}
            >
              <td className="py-3 px-3 font-medium text-foreground">{row.label}</td>
              <td className={`py-3 px-3 text-center font-semibold ${
                row.netProfit > 0 ? 'text-green-400' : row.netProfit < 0 ? 'text-red-400' : 'text-muted-foreground'
              }`}>
                {row.tradeCount === 0 ? '$0.00' : formatCurrency(row.netProfit)}
              </td>
              <td className="py-3 px-3">
                <WinPercentBar winPercent={row.winPercent} />
              </td>
              <td className={`py-3 px-3 text-center font-semibold ${
                row.totalProfits > 0 ? 'text-green-400' : 'text-muted-foreground'
              }`}>
                {formatCurrency(row.totalProfits)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Session Performance Table
 * Shows stats per trading session (London, NY, Asia, Sydney)
 */
const SessionPerformanceTable = ({ trades }) => {
  const data = useMemo(() => computeSessionSummary(trades), [trades]);
  const hasAnyTrades = data.some(d => d.tradeCount > 0);

  if (!hasAnyTrades) {
    return (
      <div className="text-center text-muted-foreground py-8">
        <p>No session data available</p>
      </div>
    );
  }

  const formatHoldTime = (minutes) => {
    if (!minutes || minutes === 0) return '--';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground border-b border-border">
            <th className="text-left py-3 px-3 font-medium">Session</th>
            <th className="text-center py-3 px-3 font-medium">Trades</th>
            <th className="text-center py-3 px-3 font-medium">Win Rate</th>
            <th className="text-center py-3 px-3 font-medium">Net P/L</th>
            <th className="text-center py-3 px-3 font-medium">Profit Factor</th>
            <th className="text-center py-3 px-3 font-medium">Avg Hold</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={row.session}
              className={`border-b border-border/50 ${i % 2 === 0 ? 'bg-muted/20' : ''}`}
            >
              <td className="py-3 px-3">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: row.color }}
                  />
                  <span className="font-medium text-foreground">{row.session}</span>
                </div>
              </td>
              <td className="py-3 px-3 text-center text-muted-foreground">
                {row.tradeCount}
              </td>
              <td className="py-3 px-3 text-center">
                {row.tradeCount > 0 ? (
                  <span className={row.winPercent >= 50 ? 'text-green-400' : 'text-red-400'}>
                    {row.winPercent.toFixed(1)}%
                  </span>
                ) : '--'}
              </td>
              <td className={`py-3 px-3 text-center font-semibold ${
                row.totalProfit > 0 ? 'text-green-400' : row.totalProfit < 0 ? 'text-red-400' : 'text-muted-foreground'
              }`}>
                {row.tradeCount > 0 ? formatCurrency(row.totalProfit) : '--'}
              </td>
              <td className={`py-3 px-3 text-center font-semibold ${
                row.profitFactor >= 1.5 ? 'text-green-400' : row.profitFactor >= 1 ? 'text-yellow-400' : 'text-red-400'
              }`}>
                {row.tradeCount > 0
                  ? row.profitFactor === Infinity ? '∞' : row.profitFactor.toFixed(2)
                  : '--'}
              </td>
              <td className="py-3 px-3 text-center text-muted-foreground">
                {formatHoldTime(row.avgHoldTimeMin)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * SessionDurationSummary
 * Combined widget: Duration buckets + Session performance
 */
const SessionDurationSummary = ({ trades = [] }) => {
  const [activeTab, setActiveTab] = React.useState('duration');

  return (
    <div className="bg-card text-card-foreground rounded-lg shadow-sm border border-border">
      {/* Tab Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h3 className="text-lg font-bold uppercase tracking-wide">Summary</h3>
        <div className="flex items-center bg-muted rounded-lg p-0.5">
          <button
            onClick={() => setActiveTab('duration')}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
              activeTab === 'duration'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            By Duration
          </button>
          <button
            onClick={() => setActiveTab('session')}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
              activeTab === 'session'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            By Session
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-2 pb-4">
        {activeTab === 'duration' ? (
          <DurationSummaryTable trades={trades} />
        ) : (
          <SessionPerformanceTable trades={trades} />
        )}
      </div>
    </div>
  );
};

export default SessionDurationSummary;
